import { posToDOMRect } from "@tiptap/core";
import { Code, CodeSmall, GoogleAds, GoogleAdsSmall } from "@wix/wix-ui-icons-common";
import htmlDataDefaults from "./../ricos-schema/legacy/statics/html.defaults.json.js";
import { HTMLData_Source } from "./../ricos-schema/next-schema/index.js";
import { quickActionsModalSections, RicosPluginToolbarNames } from "./../ricos-types/index.js";
import { changeSelectionToEndOfTextBlock } from "./../wix-rich-content-common/index.js";
import { HtmlPluginContextProvider } from "./context.js";
import { DEFAULTS_CONFIG } from "./defaults.js";
import { getAddButtons } from "./getAddButtons.js";
import { getToolbarButtons } from "./getToolbarButtons.js";
import { htmlConverter } from "./html-converter.js";
import { openFloatingPromptInput } from "./tiptap/open-floating-prompt-input.js";
import { tiptapExtensions } from "./tiptap/tiptap.js";
import { HTML_TYPE } from "./types.js";
const pluginHtml = (config) => {
  const pluginConfig = { ...DEFAULTS_CONFIG, ...config };
  return {
    config: pluginConfig,
    type: HTML_TYPE,
    pmMarkConverters: [],
    pmNodeConverters: [htmlConverter],
    tiptapExtensions,
    Provider: HtmlPluginContextProvider,
    getAddButtons: (config2, services) => getAddButtons(config2, services),
    toolbar: {
      names: [RicosPluginToolbarNames.HTML],
      getButtons: (config2, services) => getToolbarButtons(config2, services),
      resolveReferenceElement: (tiptapEditor) => {
        const selectedDOM = tiptapEditor.view.nodeDOM(tiptapEditor.state.selection.from);
        const firstNodeChild = getFirstNodeChildElement(selectedDOM);
        const aiWidgetContainer = firstNodeChild?.querySelector?.(
          "[data-hook~=ai-widget-container]"
        );
        if (aiWidgetContainer) {
          const htmlViewer = aiWidgetContainer.querySelector(
            `[data-hook~=ai-widget-html-viewer]`
          );
          const nodeId = htmlViewer?.getAttribute("data-node-id");
          return {
            getBoundingClientRect: () => {
              const htmlViewerRect = htmlViewer?.getBoundingClientRect();
              if (!htmlViewerRect) {
                return new DOMRect(0, 0, 0, 0);
              }
              const currentFloatingPrompt = nodeId ? document.querySelector(
                `[data-hook~=ai-widget-floating-prompt][data-node-id="${nodeId}"]`
              ) : null;
              const floatingPromptRect = currentFloatingPrompt?.getBoundingClientRect();
              const floatingPromptStyles = currentFloatingPrompt ? window.getComputedStyle(currentFloatingPrompt) : null;
              const isFloatingPromptVisible = currentFloatingPrompt && floatingPromptRect && floatingPromptStyles?.display !== "none" && floatingPromptRect.width > 0 && floatingPromptRect.height > 0 && floatingPromptRect.top > 0;
              if (!isFloatingPromptVisible || !floatingPromptRect) {
                return htmlViewerRect;
              }
              const top = Math.min(htmlViewerRect.top, floatingPromptRect.top);
              const left = Math.min(htmlViewerRect.left, floatingPromptRect.left);
              const right = Math.max(htmlViewerRect.right, floatingPromptRect.right);
              const bottom = Math.max(htmlViewerRect.bottom, floatingPromptRect.bottom);
              return new DOMRect(left, top, right - left, bottom - top);
            },
            // Provide context element to help with positioning calculations
            contextElement: htmlViewer
          };
        }
        if (firstNodeChild) {
          return firstNodeChild;
        }
        return {
          getBoundingClientRect: () => {
            const { state, view } = tiptapEditor;
            const { from, to } = state.selection;
            return posToDOMRect(view, from, to);
          }
        };
      }
    },
    getQuickActions: ({ pluginServices }) => {
      const isMobile = pluginServices?.context.isMobile;
      const aiWidget = {
        id: "aiWidget",
        title: "QuickActionsPlugin_ActionTitle_AiWidget",
        icon: isMobile ? Code : CodeSmall,
        section: quickActionsModalSections.Embed,
        tags: [
          "code",
          "html",
          "embed",
          "iframe",
          "script",
          "ai widget",
          "gpt",
          "chatgpt",
          "create"
        ],
        command: ({ editor, modalService }) => {
          openFloatingPromptInput({ editor, modals: modalService });
          return true;
        }
      };
      return [
        {
          id: "adSense",
          title: "QuickActionsPlugin_ActionTitle_AdSense",
          icon: isMobile ? GoogleAds : GoogleAdsSmall,
          section: quickActionsModalSections.Embed,
          tags: ["advertisement", "google ads", "google", "monetization", "ad widget", "banner ad"],
          command: ({ publicCommands, editor }) => {
            changeSelectionToEndOfTextBlock(editor);
            publicCommands.insertBlockWithBlankLines(HTML_TYPE, {
              ...htmlDataDefaults,
              source: HTMLData_Source.ADSENSE
            });
          }
        },
        {
          id: "htmlEmbed",
          title: "QuickActionsPlugin_ActionTitle_HtmlEmbed",
          icon: isMobile ? Code : CodeSmall,
          section: quickActionsModalSections.Embed,
          tags: ["code", "html", "embed", "iframe", "script"],
          command: ({ publicCommands, editor }) => {
            changeSelectionToEndOfTextBlock(editor);
            publicCommands.insertBlockWithBlankLines(HTML_TYPE, htmlDataDefaults);
          }
        },
        ...config?.enableAiMode ? [aiWidget] : []
      ];
    }
  };
};
const getFirstNodeChildElement = (selectedDOM) => {
  if (!selectedDOM || !(selectedDOM instanceof HTMLElement)) {
    return null;
  }
  const ricosNodeFirstChild = selectedDOM?.querySelector?.("[data-hook~=ricos-node]")?.firstChild ?? null;
  if (!ricosNodeFirstChild || !(ricosNodeFirstChild instanceof HTMLElement)) {
    return null;
  }
  return ricosNodeFirstChild;
};
export {
  pluginHtml
};
//# sourceMappingURL=editor.js.map

import { injectCss } from "./viewer-styles-inject.js";
injectCss();
